<template>
    <CieloLayout>
        <div class="personal-data">
            <OpenFinanceRedirect
                v-if="isPixOpenFinance && selectedBank"
                :show="bankRedirectModal"
                :selected-bank="selectedBank"
            />

            <div class="personal-data__wrapper-overview">
                <h4 class="personal-data__title" v-text="$ml.get('orderReview')" tabindex="0" />
                <div class="personal-data__wrapper-overview__container">
                    <div class="personal-data__wrapper-overview__container__content">
                        <ShippingMethodOverview />
                        <CreditCardOverview />
                        <BoletoOverview />
                        <PixOverview />
                        <PixOpenFinanceOverview />
                        <ClickToPayOverview />
                        <QrCodePayOverview />
                        <RecurringPaymentWarning />
                        <PaymentDateOverview :has-icon="true" />
                    </div>
                </div>
            </div>

            <SelectedBank
                v-if="isPixOpenFinance && selectedBank"
                :selected-bank="selectedBank"
                style="margin-bottom: 12px;"
            />

            <div class="personal-data__wrapper-form">
                <!-- Dados do comprador -->
                <h4 class="personal-data__title" v-text="$ml.get('personalDataSubtitle')" tabindex="0" />

                <CieloInputV2
                    :autofocus="true"
                    :borderless="false"
                    name="Name"
                    :label="$ml.get('fullName')"
                    v-model="Name"
                    v-validate="'required|fullname'"
                    :data-vv-as="$ml.get('fullName')"
                    type="text"
                    :error="errors.first('Name')"
                    class="personal-data-input"
                />
                <CieloInputV2
                    class="personal-data-input"
                    :autofocus="false"
                    :borderless="false"
                    name="Email"
                    :label="$ml.get('email')"
                    v-model="Email"
                    v-validate="'required|safe-mail'"
                    :data-vv-as="$ml.get('email')"
                    type="email"
                    :error="errors.first('Email')"
                />

                <div class="personal-data__wrapper-form__security-card--phone">
                    <!-- <CieloSelectV2
                        @change="selectCountry"
                        :title="$ml.get('countryCode')"
                        :label="$ml.get('selectCountryCode')"
                        :option-items="countryCodes"
                        v-model="CountryCode"
                        :selected-item="selectedCountry"
                        other-class="cielo-select__container--phone"
                        custom-class="cielo-select--phone"
                    /> TODO - descomentar quando ajustar o portador Estrangeiro e for para produção definitivo -->
                    <CieloInputV2
                        :autofocus="false"
                        :borderless="false"
                        name="Phone"
                        :label="$ml.get('cellPhone')"
                        v-model="Phone"
                        v-validate="phoneMaxLength"
                        :data-vv-as="$ml.get('cellPhone')"
                        type="tel"
                        :error="errors.first('Phone')"
                        :mask="phoneMask"
                        custom-class="cielo-input-container__phone"
                        class="phone-custom--v2"
                    />
                </div>

                <div class="personal-data__wrapper-form__security-card">
                    <CieloSelectV2
                        @change="selectDocumentType"
                        :title="$ml.get('documentType')"
                        :label="$ml.get('selectDocumentType')"
                        :option-items="documentTypes"
                        :selected-item="selectedDocumentType"
                        v-model="IdentityType"
                        other-class="cielo-select__container--documentType"
                        :can-translate="true"
                    />
                    <CieloInputV2
                        style="width: 100%; margin-bottom: 0px"
                        :autofocus="false"
                        :borderless="false"
                        name="Identity"
                        :label="$ml.get('identity')"
                        v-model="Identity"
                        v-validate="validationRule"
                        :data-vv-as="$ml.get('identity')"
                        :type="isNational ? 'tel' : 'text'"
                        :error="errors.first('Identity')"
                        :mask="getMask"
                        :maxlength="getMaxLength"
                    />
                </div>
            </div>
            <div class="personal-data__wrapper-billing">
                <h4
                    v-if="!this.isPixOpenFinance"
                    class="personal-data__title mb-3"
                    v-text="$ml.get('billingAddress')"
                    tabindex="0"
                />

                <template v-if="!this.isPixOpenFinance">
                    <SameShippingAddress v-if="productHasShipping" :same-as-shipping="true" />
                    <BillingAddress :address-errors="addressErrors" />
                </template>

                <div class="personal-data__terms-info">
                    <CieloCheckBoxV2
                        class="justify-content-start"
                        v-model="agreement"
                        :clickable-slot="true"
                        :text-aria="$ml.get('termsPreffix') + $ml.get('termsSuffix')"
                    >
                        <span v-text="$ml.get('termsPreffix')" />&nbsp;
                        <a target="_blank" :href="showTerms" v-text="$ml.get('termsSuffix')" tabindex="0" />
                    </CieloCheckBoxV2>
                </div>

                <BankRedirectWarning v-if="isPixOpenFinance && selectedBank" :selected-bank="selectedBank" />

                <!-- Botões inferiores -->
                <ButtonsFinishPaymentV2
                    :disable-button-next="!enableFinishButton"
                    :loading="loading"
                    @click-next="finishPayment"
                    @click-previous="goToPreviousPage"
                    :is-qrcode="isQrcode"
                    :is-pix="isPix"
                    :is-pix-open-finance="isPixOpenFinance"
                />
            </div>
        </div>
    </CieloLayout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import ProductHelper from '@/helpers/productHelper';
import Swal from 'sweetalert2';

const CieloInputV2 = () =>
	import(
        /* webpackChunkName: "cielo-input" */ '@/components/ClickToPay/template/CieloInputV2'
	);
const ButtonsFinishPaymentV2 = () =>
	import(
        /* webpackChunkName: "cielo-button" */ '@/components/template/ButtonsFinishPaymentV2'
	);

import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
import validator from '@/helpers/validation-rules/CpfCnpjValidation';
import SameShippingAddress from '@/components/Billing/SameShippingAddress';
import BillingAddress from '@/components/Billing/BillingAddress';
import PaymentProcessor from '@/services/payment/PaymentProcessor';
import vars from '@/config/vars';
import FullNameValidation from '@/helpers/validation-rules/FullNameValidation';
import SafeMailValidation from '@/helpers/validation-rules/SafeMailValidation';
import CieloLayout from '@/components/layout/CieloLayout.vue';
import CieloCheckBoxV2 from '@/components/ClickToPay/template/CieloCheckBoxV2';
import CreditCardOverview from '@/components/OrderOverview/components/CreditCardOverview';
import BoletoOverview from '@/components/OrderOverview/components/BoletoOverview';
import PixOverview from '@/components/OrderOverview/components/PixOverview';
import PixOpenFinanceOverview from '@/components/OrderOverview/components/PixOpenFinanceOverview';
import PaymentDateOverview from '@/components/OrderOverview/components/PaymentDateOverview';
import ClickToPayOverview from '@/components/OrderOverview/components/ClickToPayOverview';
import ShippingMethodOverview from '@/components/OrderOverview/components/ShippingMethodOverview';
import QrCodePayOverview from '@/components/OrderOverview/components/QrCodePayOverview';
import RecurringPaymentWarning from '@/components/RecurringPaymentWarning';
import BankRedirectWarning from '@/components/BankRedirectWarning';
import SelectedBank from '@/components/SelectedBank';
import OpenFinanceRedirect from '@/components/OpenFinanceRedirect';
import CieloSelectV2 from '@/components/ClickToPay/template/CieloSelectV2/CieloSelectV2.vue';
import PaymentTypes from '@/models/PaymentTypes';
import countries from '@/mockedCountries.js';

export default {
	components: {
		CieloInputV2,
		CieloSelectV2,
		SameShippingAddress,
		BillingAddress,
		ButtonsFinishPaymentV2,
		CieloLayout,
		CieloCheckBoxV2,
		CreditCardOverview,
		BoletoOverview,
		PixOverview,
		ClickToPayOverview,
		ShippingMethodOverview,
		QrCodePayOverview,
		RecurringPaymentWarning,
		BankRedirectWarning,
		SelectedBank,
		OpenFinanceRedirect,
		PixOpenFinanceOverview,
		PaymentDateOverview,
	},

	computed: {
		...mapGetters({
			shippingType: 'shippingType/getType',
			isNational: 'billingAddress/getIsNational',
			selectedBank: 'pixOpenFinancePayment/getSelectedBank',
			bankRedirectModal: 'pixOpenFinancePayment/getBankRedirectModal',
			availablePixTypes: 'paymentTypes/getPixTypes',
		}),

		phoneMaxLength() {
			return this.selectedCountryIso && this.selectedCountryIso.iso === 'BR' ? 'required|numeric|min:11' : 'required|numeric';
		},

		phoneMask() {
			// return this.selectedCountryIso && this.selectedCountryIso.iso === 'BR' ? ['(##) #####-####'] : null; TODO - descomentar quando ajustar o portador Estrangeiro e for para produção definitivo
			return ['(##) #####-####'];
		},

		getMask() {
			if (this.selectedDocumentType === 'Estrangeiro') {
				return null;
			} else if (this.selectedDocumentType === 'CPF') {
				return '###.###.###-##';
			} else if (this.selectedDocumentType === 'CNPJ') {
				return '##.###.###/####-##';
			}
			return null;
		},

		getMaxLength() {
			if (this.selectedDocumentType === 'Estrangeiro') {
				return 14;
			}
			return null;
		},

		isQrcode: (self) =>
			self.$store.state.payment.PaymentType == PaymentTypes.QRCODE ||
			self.$store.state.payment.PaymentType == PaymentTypes.QRCODE_DEBIT,

		isPix: (self) => self.$store.state.payment.PaymentType == PaymentTypes.PIX,

		isPixOpenFinance: (self) => self.$store.state.payment.PaymentType == PaymentTypes.PIX_OPEN_FINANCE,

		productHasShipping: () => ProductHelper.productHasShipping(),

		enableFinishButton() {
			if (!this.Name || !this.Email || !this.Phone || !this.Identity)
				return false;
			if (this.errors.items.length > 0) return false;
			if (!this.agreement) return false;
			return true;
		},
		/**
		 * Dados a serem sincronizados com o storage
		 */
		Name: {
			get: (self) => self.$store.state.customer.Name,
			set(value) {
				this.$store.commit('customer/setName', value);
			},
		},
		Email: {
			get: (self) => self.$store.state.customer.Email,
			set(value) {
				this.$store.commit('customer/setEmail', value);
			},
		},
		Phone: {
			get: (self) => self.$store.state.customer.Phone,
			set(value) {
				this.$store.commit('customer/setPhone', value);
			},
		},
		Identity: {
			get: (self) => self.$store.state.customer.Identity,
			set(value) {
				this.$store.commit('customer/setIdentity', value);
			},
		},
		IdentityType: {
			get: (self) => self.$store.state.customer.IdentityType,
			set(value) {
				this.$store.commit('customer/setIdentityType', value);
			},
		},
		PhoneCountry: {
			get: (self) => self.$store.state.customer.PhoneCountry,
			set(value) {
				this.$store.commit('customer/setPhoneCountry', value);
			},
		},
		CountryCode: {
			get: (self) => self.$store.state.customer.CountryCode,
			set(value) {
				this.$store.commit('customer/setCountryCode', value);
			},
		},
		AcceptAds: {
			get: (self) => self.$store.state.customer.AcceptAds,
			set(value) {
				this.$store.commit('customer/setAcceptAds', value);
			},
		},
	},

	mounted() {
		if (this.Name || this.Email || this.Phone || this.Identity) {
			this.$validator.validate();
		}
		this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
		this.$validator.extend('fullname', FullNameValidation);
		this.$validator.extend('safe-mail', SafeMailValidation);
		PaymentProcessor.multiLanguage = this.$ml;
	},

	created() {
		this.$validator.extend('cpf', {
			validate: (value) => {
				const isValid = validator.validate(value) && value.length === 11;
				return isValid;
			},
			getMessage: (field) => `O campo ${field} deve ser um CPF válido`,
		});

		this.$validator.extend('cnpj', {
			validate: (value) => {
				const isValid = validator.validate(value) && value.length === 14;
				return isValid;
			},
			getMessage: (field) => `O campo ${field} deve ser um CNPJ válido`,
		});
	},

	data() {
		return {
			showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
			showPrivacy: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Politica%20de%20Privacidade.pdf`,
			loading: false,
			addressErrors: {},
			agreement: false,
			selectedCountryIso: {},
			countryCodes: countries.map((country) => ({
				Text: country.text,
				Value: country.iso,
				Code: country.code,
			})),
			selectedCountry: 'BR',
			validationRule: 'required|max:14',
			documentTypes: [
				{ Text: 'cpf', Value: 'CPF' },
				{ Text: 'cnpj', Value: 'CNPJ' },
				{ Text: 'foreign', Value: 'FOREIGN' },
			],
			selectedDocumentType: this.$store.state.customer.IdentityType || 'CPF',
		};
	},

	methods: {
		goToPreviousPage() {
			if ((this.isPix || this.isPixOpenFinance) && this.availablePixTypes.length > 1) {
				this.$store.dispatch('pipelineV2/pushTo', 'ChangePixType');
			} else {
				this.$store.dispatch('pipelineV2/pushPrevious');
			}
		},

		async finishPayment() {
			if (!this.loading) {
				this.loading = true;

				let isValid = await this.$validator.validateAll();
				if (!isValid) {
					this.loading = false;
					return;
				}

				if (!this.isPixOpenFinance) {
					const billingAddress = this.$store.state.billingAddress;

					if (!billingAddress.SameAsShippingAddress && !billingAddress.State) {
						this.loading = false;
						return await Swal.fire({
							type: 'warning',
							text: this.$ml.get('chooseYourState'),
							confirmButtonText: this.$ml.get('addressUnderstood'),
						});
					}
				}

				await PaymentProcessor.Process();
				this.loading = false;
			}
		},

		selectCountry(selectedCountryIso) {
			this.selectedCountryIso = { iso: selectedCountryIso };
			const selectedCountry = this.countryCodes.find(country => country.Value === selectedCountryIso);
			this.$store.commit('customer/setPhoneCountry', selectedCountry.Code);
		},

		selectDocumentType(value) {
			this.selectedDocumentType = value;

			const type = value === 'FOREIGN' ? 'foreign' : 'national';
			this.$store.commit('billingAddress/setType', type);
		},

		updateValidationRule(value) {

			if (value === 'CPF') {
				this.validationRule = 'required|numeric|max:11|cpf';
			} else if (value === 'CNPJ') {
				this.validationRule = 'required|numeric|min:14|cnpj';
			} else {
				this.validationRule = 'required|max:14';
			}
		},
	},
	watch: {
		selectedDocumentType(newVal) {
			this.$store.commit('customer/setIdentityType', newVal);
			this.updateValidationRule(newVal);
		},
	},
};
</script>

<style lang="scss" scoped>
.personal-data {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 20px;

	.cielo-input {
		margin-bottom: 0px;
	}

	&__wrapper-form {
		display: flex;
		flex-direction: column;
		width: 100%;
		gap: 20px;

		&__security-card {
			display: flex;
			gap: 20px;
			width: 100%;
			margin-top: 50px;

			@media screen and (max-width: 1024px) {
				flex-direction: column;
				margin-top: 0px;
			}

			&--phone {
				width: 100%;
				display: flex;
				position: relative;
				margin-bottom: 10px;

				.cielo-input {
					width: 100%;
				}

				@media screen and (max-width: 1024px) {
					flex-direction: row;
				}
			}
		}
	}

	&__wrapper-billing {
		margin-top: 12px;
		width: 100%;
	}

	&__wrapper-overview {
		display: none;

		@media screen and (max-width: 1024px) {
			display: flex;
			flex-direction: column;
			gap: 20px;
			width: 100%;
		}

		&__container {
			background: #eff1f3;
			border: 1px solid #eaeef3;
			border-radius: 8px;
			padding: 16px;

			&__content {
				gap: 20px;
				display: flex;
				flex-direction: column;
			}
		}
	}

	&__wrapper-billing {
		margin-top: 12px;
	}

	&__title {
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: 24px;
		color: #2f363e;
		margin: 0 0 0 0;
	}

	&__terms-info {
		display: flex;
		flex-direction: column;
		font-size: 14px;
		color: #6c6c6c;
		text-align: left;
		margin-top: 32px;
		margin-bottom: 32px;
		gap: 20px;
	}
}
</style>
